"use client";

import { Loader2 } from "lucide-react";
import type { FormEvent } from "react";
import { useToolStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface AutoInputFormProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e: FormEvent) => void;
  loading: boolean;
  // When a bulk paste is in flight, e.g. "Citing 3 of 10...". Replaces the button label.
  progressLabel?: string;
  // Replaces the mixed-source example lines in the empty box, so a page about one kind of
  // identifier (ISBNs, DOIs) shows that kind. Language-neutral numbers only.
  placeholder?: string;
}

const DEFAULT_PLACEHOLDER =
  "https://example.com/article\n10.1000/xyz123\n29083010 (PubMed ID)\narXiv:2202.01037\n978-0-123456-78-9\nor paste BibTeX...";

export function AutoInputForm({
  value,
  onChange,
  onSubmit,
  loading,
  progressLabel,
  placeholder = DEFAULT_PLACEHOLDER,
}: AutoInputFormProps) {
  const s = useToolStrings();
  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <div className="space-y-2">
        <Label htmlFor="auto-input">{s.autoLabel}</Label>
        <Textarea
          className="min-h-[100px] resize-y font-mono text-sm"
          id="auto-input"
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          value={value}
        />
        <p className="text-muted-foreground text-xs">{s.autoHint}</p>
      </div>
      <Button disabled={loading} type="submit">
        {loading ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            {progressLabel ?? s.generating}
          </>
        ) : (
          s.generate
        )}
      </Button>
    </form>
  );
}
